import React from 'react';
import { useUserContext } from '../../hooks/userContext';
import { CircularProgress, Box, Typography } from '@mui/material';

const getEmailVerifiedFlag = (value: unknown): boolean | null => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

interface OnboardingGuardProps {
  children: React.ReactNode;
  requiredStatus?: string;
  redirectTo?: string;
}

export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ 
  children, 
  requiredStatus = 'active',
  redirectTo 
}) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // Check if user has the required status to access this step
  if (user && user.id) {
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    
    // If user doesn't have the required status, don't render the step
    if (requiredStatus && userStatus !== requiredStatus) {
      return (
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="200px"
          p={3}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Please complete the previous step first
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current status: {userStatus}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required status: {requiredStatus}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export const EmailVerificationGuard: React.FC<{ children: React.ReactNode; userAccountId?: string | null }> = ({ children, userAccountId }) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // Allow access in resume registration mode (when userAccountId is 'resume-registration')
  if (userAccountId === 'resume-registration') {
    return <>{children}</>;
  }

  // Only show email verification step if user status is pending_email_verification
  if (user && user.id) {
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    const emailVerifiedFlag = getEmailVerifiedFlag(user.emailVerified);
    if (userStatus !== 'pending_email_verification' && emailVerifiedFlag !== false) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" p={3}>
          <Typography variant="h6" color="text.secondary">
            Email verification step not accessible
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current status: {userStatus}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export const ProfileCompletionGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // Only show profile completion step if user status is pending_profile_completion
  if (user && user.id) {
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    if (userStatus !== 'pending_profile_completion') {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" p={3}>
          <Typography variant="h6" color="text.secondary">
            Profile completion step not accessible
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current status: {userStatus}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

// Designer-specific guards
export const DesignerEmailVerificationGuard: React.FC<{ children: React.ReactNode; userAccountId?: string | null }> = ({ children, userAccountId }) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // Allow access in resume registration mode (when userAccountId is 'resume-registration')
  if (userAccountId === 'resume-registration') {
    return <>{children}</>;
  }

  // Only show email verification step for designers if user status is pending_email_verification
  if (user && user.id && user.accountType === 'Designer') {
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    const emailVerifiedFlag = getEmailVerifiedFlag(user.emailVerified);
    if (userStatus !== 'pending_email_verification' && emailVerifiedFlag !== false) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" p={3}>
          <Typography variant="h6" color="text.secondary">
            Email verification step not accessible
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current status: {userStatus}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export const DesignerProfileCompletionGuard: React.FC<{ children: React.ReactNode; step: number }> = ({ children, step }) => {
  const { user, loading } = useUserContext();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // Only show designer profile steps if user is a designer and status is pending_profile_completion
  if (user && user.id && user.accountType === 'Designer') {
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    
    // Allow access to profile completion steps for designers
    if (userStatus === 'pending_profile_completion') {
      return <>{children}</>;
    }
    
    // If user is already active, they should be redirected to dashboard
    if (userStatus === 'active') {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" p={3}>
          <Typography variant="h6" color="text.secondary">
            Registration already completed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can access your designer dashboard
          </Typography>
        </Box>
      );
    }
    
    // For other statuses, show not accessible message
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" p={3}>
        <Typography variant="h6" color="text.secondary">
          Profile step not accessible
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Current status: {userStatus}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Step: {step}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
